import type { CSSProperties, ReactNode } from 'react';
import type { BlockField } from '@/blocks/domain/types/BlockContent/BlockField';
import { cssPxFromPdfPt, FIELD_LIST_EDITOR_PX, FIELD_LIST_EXPORT_PT } from '@/common/domain/fieldListSpacing';
import { SectionInsetFrame } from './EditorInsetFrame';

export interface FlatFieldListProps {
  fields: BlockField[];
  exported: boolean;
  renderEditorField: (field: BlockField) => ReactNode;
  renderExportContent: (field: BlockField) => ReactNode;
}

function fieldBasis(field: BlockField): string {
  return field.width === 'half' ? '50%' : '100%';
}

export function FlatFieldList({ fields, exported, renderEditorField, renderExportContent }: FlatFieldListProps) {
  if (exported) {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {fields.map((field) => {
          const flexBasis = fieldBasis(field);
          const itemStyle: CSSProperties = {
            flex: `0 0 ${flexBasis}`,
            maxWidth: flexBasis,
            boxSizing: 'border-box',
            textAlign: field.align,
            paddingBottom: cssPxFromPdfPt(FIELD_LIST_EXPORT_PT.flatFieldPaddingBottom),
            paddingRight: field.width === 'half' ? cssPxFromPdfPt(FIELD_LIST_EXPORT_PT.halfColumnPaddingEnd) : 0
          };
          return (
            <div key={field.id} data-testid={`document-render:export-field:${field.id}`} data-field-id={field.id} style={itemStyle}>
              {renderExportContent(field)}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <SectionInsetFrame testId="document-render:fields:section-default">
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {fields.map((field) => {
          const flexBasis = fieldBasis(field);
          const itemStyle: CSSProperties = {
            flex: `0 0 ${flexBasis}`,
            maxWidth: flexBasis,
            boxSizing: 'border-box',
            textAlign: field.align,
            paddingRight: field.width === 'half' ? FIELD_LIST_EDITOR_PX.halfColumnPaddingEnd : 0
          };
          return (
            <div key={field.id} style={itemStyle}>
              {renderEditorField(field)}
            </div>
          );
        })}
      </div>
    </SectionInsetFrame>
  );
}
